import React, { useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { PageLightbox } from './PageLightbox';
import { lookInsidePages } from '../data/lookInsidePages';
import { useElementWidth } from '../hooks/useElementWidth';
const EASE = [0.23, 1, 0.32, 1] as const;
const CAPTION_SPACE = 64;

/**
 * "Look inside" — carrusel con páginas reales del Volumen 4.
 * Mobile: una página centrada con las vecinas asomando. Desktop: tres,
 * la central más grande. Sin autoplay, en loop. Click abre el lightbox.
 */
export function LookInside() {
  const {
    ref,
    width
  } = useElementWidth<HTMLDivElement>();
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState<number | null>(null);
  const dragged = useRef(false);
  const reduceMotion = useReducedMotion();
  const n = lookInsidePages.length;
  const isDesktop = width >= 768;
  const cardW = isDesktop ? Math.min(300, width * 0.3) : width * 0.75;
  const sideScale = isDesktop ? 0.85 : 0.92;
  const gap = isDesktop ? 28 : 12;
  const step = cardW * (1 + sideScale) / 2 + gap;
  const cardH = cardW * 16 / 9;
  const go = (dir: 1 | -1) => setActive(a => (a + dir + n) % n);
  const arrowClass = 'absolute z-20 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[#1E2140] shadow-[0_6px_20px_rgba(30,33,64,0.14)] transition-colors duration-150 ease-out hover:bg-[#F2694B] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#F2694B]/50 md:flex';
  return <section id="look-inside" aria-labelledby="look-inside-heading" className="w-full overflow-hidden bg-[#F6EFE4] font-sans text-[#1E2140]" data-id="element-175" data-mp="3ign1lvmmxsvy">
      <div className="mx-auto max-w-5xl px-5 pt-14 sm:pt-20" data-id="element-176" data-mp="3sf2ti0thl66i">
        <div className="text-center" data-id="element-177" data-mp="34qb57kj0p7z0">
          <p className="text-[12px] font-extrabold uppercase tracking-[1.8px] text-[#F2694B]" data-id="element-178" data-mp="34p8fhfxflbko">
            Look inside
          </p>
          <h2 id="look-inside-heading" className="mt-3 font-poppins text-[28px] font-semibold leading-tight sm:text-[42px]" data-id="element-179" data-mp="13ov3jt1paf68">
            Open it tonight. Teach it tomorrow.
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-[#1E2140]/80 sm:text-lg" data-id="element-180" data-mp="1de50bm7hom0o">
            Real pages from Volume 4: Inversions, Pops &amp; Standing Basics.
          </p>
        </div>
      </div>

      <div ref={ref} role="region" aria-roledescription="carousel" aria-label="Volume 4 pages" className="relative mx-auto mt-10 w-full max-w-5xl" style={{
      height: width ? cardH + CAPTION_SPACE : undefined
    }} data-id="element-181" data-mp="kr0x9mryihrj">
        {width > 0 && <>
            <motion.div className="absolute inset-0 cursor-grab active:cursor-grabbing" style={{
          touchAction: 'pan-y'
        }} drag="x" dragConstraints={{
          left: 0,
          right: 0
        }} dragElastic={0.18} onDragStart={() => {
          dragged.current = true;
        }} onDragEnd={(_, info) => {
          if (info.offset.x < -50 || info.velocity.x < -400) go(1);else if (info.offset.x > 50 || info.velocity.x > 400) go(-1);
        }} data-id="element-182" data-mp="yqjuvln56apl">
              {lookInsidePages.map((page, i) => {
            let d = (i - active + n) % n;
            if (d > n / 2) d -= n;
            const isActive = d === 0;
            const visible = Math.abs(d) <= 1;
            return <motion.div key={page.src} className="absolute left-1/2 top-0" style={{
              width: cardW,
              marginLeft: -cardW / 2,
              transformOrigin: 'top center',
              zIndex: isActive ? 10 : 5,
              pointerEvents: visible ? 'auto' : 'none'
            }} initial={false} animate={{
              x: d * step,
              scale: isActive ? 1 : sideScale,
              opacity: visible ? isActive ? 1 : 0.6 : 0
            }} transition={{
              duration: reduceMotion ? 0 : 0.28,
              ease: EASE
            }} aria-hidden={!visible} data-id="element-183" data-mp="1wlcqgc23ysuj" data-mpi={i}>
                    <button type="button" tabIndex={visible ? 0 : -1} aria-label={isActive ? `Open full screen: ${page.caption}` : `Show page ${i + 1}: ${page.caption}`} onPointerDown={() => {
                dragged.current = false;
              }} onClick={() => {
                if (dragged.current) return;
                if (isActive) setOpen(i);else setActive(i);
              }} className="block w-full cursor-zoom-in overflow-hidden rounded-[16px] border border-[#EDE3D3] bg-white shadow-[0_2px_4px_rgba(30,33,64,0.05),0_14px_34px_rgba(30,33,64,0.12)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#F2694B]/50" style={{
                height: cardH
              }} data-id="element-184" data-mp="367qlgr5zee90">
                      <img src={page.src} alt={page.caption} loading={i === 0 ? 'eager' : 'lazy'} draggable={false} className="h-full w-full select-none object-contain" data-id="element-185" data-mp="2rl1n4sngu747" />
                    </button>
                    <p className="mx-auto mt-3 line-clamp-2 max-w-[92%] text-center text-[14px] font-semibold leading-snug sm:text-[15px]" data-id="element-186" data-mp="3ad3xxndfet6j">
                      {page.caption}
                    </p>
                  </motion.div>;
          })}
            </motion.div>

            <button type="button" onClick={() => go(-1)} aria-label="Previous page" className={`${arrowClass} left-2`} style={{
          top: cardH / 2
        }} data-id="element-187" data-mp="22ys1yk8qbmr0">
              <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-188" data-mp="2gsj95lyzmujk" />
            </button>
            <button type="button" onClick={() => go(1)} aria-label="Next page" className={`${arrowClass} right-2`} style={{
          top: cardH / 2
        }} data-id="element-189" data-mp="2lrm60j4tdugg">
              <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-190" data-mp="2vqli6bosg887" />
            </button>
          </>}
      </div>

      <div className="mx-auto flex max-w-5xl flex-col items-center px-5 pb-14 pt-4 sm:pb-20" data-id="element-191" data-mp="2zq2mv2we51cb">
        <div className="flex items-center gap-1" role="group" aria-label="Choose a page" data-id="element-192" data-mp="11d2dmhwcbpde">
          {lookInsidePages.map((page, i) => <button key={page.src} type="button" onClick={() => setActive(i)} aria-label={`Go to page ${i + 1}`} aria-current={i === active} className="flex h-7 w-7 items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F2694B]/60 rounded-full" data-id="element-193" data-mp="xt1xlseypmjd" data-mpi={i}>
              <span className={`block h-2.5 w-2.5 rounded-full transition-colors duration-200 ease-out ${i === active ? 'bg-[#F2694B]' : 'bg-[#EDE3D3]'}`} data-id="element-194" data-mp="k03v7m43qu6b" />
            </button>)}
        </div>

        <p className="mt-5 text-center text-sm text-[#1E2140]/75" data-id="element-195" data-mp="17py6w0coqcoz">
          Volume 4 of 6 shown. Every volume follows the same format.
        </p>
        <a href="#pricing" className="mt-5 inline-flex min-h-[52px] items-center justify-center rounded-full bg-[#F2694B] px-8 text-base font-bold text-[#1E2140] shadow-[0_8px_22px_-8px_rgba(242,105,75,0.7)] transition-[background-color,box-shadow] duration-150 ease-out hover:bg-[#F5826A] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#F2694B]/40" data-id="element-196" data-mp="3shguby7kassb">
          Get the full pack
        </a>
      </div>

      <AnimatePresence data-id="element-197" data-mp="1himn525v0vm0">
        {open !== null && <PageLightbox page={lookInsidePages[open]} onClose={() => setOpen(null)} data-id="element-198" data-mp="1mg62k5eanefj" />}
      </AnimatePresence>
    </section>;
}